/**
 * Katalog modułów administracyjnych — JEDNO źródło prawdy dla panelu `/admin`.
 *
 * Dotąd ta sama wiedza („co jest w administracji i jakie uprawnienie tym rządzi") była
 * powtórzona w trzech miejscach: w menu bocznym, w bramkach tras w App.js i w kafelkach
 * pulpitu. Trzy kopie znaczyły trzy okazje do rozjazdu — pozycja mogła zostać w menu po
 * zmianie uprawnienia albo zniknąć z pulpitu, choć trasa dalej działała.
 *
 * Każdy wpis opisuje moduł kompletnie: nazwę, po co jest, trasę, uprawnienie które go
 * otwiera oraz — tam gdzie moduł to rozróżnia — czynności zawężone do posiadaczy
 * mocniejszego klucza. To ostatnie jest treścią widoczną na kafelku: pracownik ma z panelu
 * wyczytać, co wolno JEMU, a nie dowiadywać się tego przez odmowę przy kliknięciu.
 */

// UWAGA: „Konta przepustek" (kartoteki + wystawianie przepustek) NIE są modułem panelu —
// to codzienna praca obsługi i mieszkają jako zwykła pozycja menu (trasa `portalAccounts`),
// bez PIN-u. Wyprowadzone z panelu decyzją klienta 2026-08-07.
export const ADMIN_MODULES = [
    {
        key: "adminUsers",
        routeKey: "adminUsers",
        title: "Użytkownicy",
        description: "Konta pracowników Krosno Glass, ich typ, stan i przypisane uprawnienia.",
        permission: "UsersManage",
        permissionLabel: "Zarządzanie kontami",
        icon: "FiUsers",
        actions: [
            { label: "Podgląd kont", requires: "UsersManage" },
            { label: "Nadawanie uprawnień", requires: "UsersManage" },
            { label: "Ustawianie PIN-u do panelu", requires: "AdminOnly" },
        ],
    },
    {
        key: "adminAccessGroups",
        routeKey: "adminAccessGroups",
        title: "Grupy uprawnień",
        description:
            "Zestawy uprawnień nadawane całej randze naraz — zmiana w grupie działa "
            + "natychmiast na wszystkich jej członków.",
        permission: "UsersManage",
        permissionLabel: "Zarządzanie kontami",
        icon: "FiShield",
        actions: [
            { label: "Podgląd zawartości grup", requires: "UsersManage" },
            { label: "Zmiana uprawnień grupy", requires: "UsersManage" },
            { label: "Zakładanie i usuwanie własnych grup", requires: "UsersManage" },
        ],
    },
    {
        key: "adminWhitelist",
        routeKey: "adminWhitelist",
        title: "Whitelista logowania",
        description: "Kto w ogóle może zalogować się do Biura Przepustek — stale albo terminowo.",
        permission: "WhitelistManage",
        permissionLabel: "Whitelista",
        icon: "FiUserCheck",
        actions: [
            { label: "Podgląd wpisów", requires: "WhitelistManage" },
            { label: "Dodawanie i odbieranie dostępu", requires: "WhitelistManage" },
        ],
    },
    {
        key: "adminZones",
        routeKey: "adminZones",
        title: "Strefy dostępu",
        description:
            "Obszary zakładu, ich opiekunowie i pracownicy uprawnieni do akceptowania wniosków strefy.",
        permission: "ZoneManage",
        permissionLabel: "Strefy dostępu",
        icon: "FiMap",
        actions: [
            { label: "Podgląd stref", requires: "ZoneManage" },
            { label: "Przypisywanie opiekunów i osób", requires: "ZoneManage" },
        ],
    },
    {
        key: "adminCompanies",
        routeKey: "adminCompanies",
        title: "Firmy zewnętrzne",
        description:
            "Firmy z własnym panelem: same prowadzą kartoteki swoich pracowników, "
            + "wnioskują o przepustki w ich imieniu i odnawiają wygasające.",
        permission: "CompaniesManage",
        permissionLabel: "Firmy zewnętrzne",
        icon: "FiBriefcase",
        actions: [
            { label: "Zakładanie i edycja firm", requires: "CompaniesManage" },
            { label: "Konta przedstawicieli (zaproszenie e-mailem)", requires: "CompaniesManage" },
            { label: "Przypinanie istniejących użytkowników do firmy", requires: "CompaniesManage" },
        ],
    },
    {
        key: "adminLoginHistory",
        routeKey: "adminLoginHistory",
        title: "Historia logowań i dziennik zdarzeń",
        description:
            "Logowania udane i odrzucone oraz pełny ślad działań: decyzje, rozliczenia, "
            + "weryfikacje na bramie, odsłonięcia danych.",
        permission: "LoginHistoryView",
        permissionLabel: "Historia logowań",
        icon: "FiClock",
        actions: [
            { label: "Podgląd logowań i zdarzeń", requires: "LoginHistoryView" },
            { label: "Raporty XLSX i PDF", requires: "LoginHistoryView" },
        ],
    },
    {
        key: "adminDataAccess",
        routeKey: "adminDataAccess",
        title: "Dziennik dostępu do danych",
        description:
            "Kto, kiedy co do sekundy, z jakiego adresu i z jakiej przeglądarki oglądał czyje "
            + "dane osobowe — łącznie z wizytatorami i wejściami z linków bez logowania.",
        permission: "DataAccessJournal",
        permissionLabel: "Dziennik dostępu do danych",
        icon: "FiEye",
        actions: [
            { label: "Podgląd wszystkich odczytów danych", requires: "DataAccessJournal" },
            { label: "Kto oglądał dane wskazanej osoby", requires: "DataAccessJournal" },
            { label: "Raporty XLSX i PDF", requires: "DataAccessJournal" },
        ],
    },
    {
        key: "adminMessages",
        routeKey: "adminMessages",
        title: "Treści wiadomości",
        description: "Teksty SMS-ów i e-maili wysyłanych przez system, ze zmiennymi i podglądem długości.",
        permission: "SettingsManage",
        permissionLabel: "Ustawienia aplikacji",
        icon: "FiMessageSquare",
        actions: [
            { label: "Edycja treści i tematów", requires: "SettingsManage" },
            { label: "Wyłączanie pojedynczych komunikatów", requires: "SettingsManage" },
        ],
    },
    {
        key: "adminSettings",
        routeKey: "adminSettings",
        title: "Ustawienia procesu",
        description:
            "Okna weryfikacji na bramie, karencja PDF, przełączniki powiadomień i zasady logowania.",
        permission: "SettingsManage",
        permissionLabel: "Ustawienia aplikacji",
        icon: "FiSliders",
        actions: [
            { label: "Zmiana parametrów procesu", requires: "SettingsManage" },
            { label: "Przełączniki powiadomień SMS i e-mail", requires: "SettingsManage" },
        ],
    },
    {
        key: "adminO365",
        routeKey: "adminO365",
        title: "Office 365",
        description: "Konfiguracja logowania przez konto służbowe (tenant i identyfikator aplikacji).",
        permission: "ConfigManage",
        permissionLabel: "Konfiguracja O365",
        icon: "FiCloud",
        actions: [{ label: "Zmiana konfiguracji tenanta", requires: "ConfigManage" }],
    },
];

/** Klucze uprawnień otwierające cokolwiek w panelu — służą do pokazania samej pozycji w menu. */
export const ADMIN_PERMISSION_KEYS = [
    ...new Set(ADMIN_MODULES.map((module) => module.permission)),
];
